import { useContext, useEffect, useState } from "react";
import CustomDataGrid from "../components/CustomDataGrid";
import { PAGE_SIZE } from "../constants/Constants";
import { Button, Chip, Container, Divider, Grid, Paper, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import moment from "moment";
import { AuthContext } from "../context/AuthProvider";
import { format } from "date-fns";
import { forEach } from "lodash";
import { fetchWithInterceptor } from "../utils/fetchWithInterceptor";

const DEFAULT_FILTER = {
  date: null,
  sku: "",
};

const DEFAULT_ORDER = [
  { field: 'createdAt', sort:  "desc" }, 
];

const ContinueSellingChangeLog = () => {
  const [filter, setFilter] = useState(DEFAULT_FILTER);
  const [isLoading, setIsLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
  });
  const [sortModel, setSortModel] = useState(DEFAULT_ORDER);

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);

  const authContext = useContext(AuthContext)

  const handleReset = () => {
    setFilter(DEFAULT_FILTER);
    setSortModel(DEFAULT_ORDER);
  };

  const getOrderBy = () => {
    const orderBy = [];
    forEach(sortModel, (model) => {
      orderBy.push({ [model.field]: model.sort });
    });
    return JSON.stringify(orderBy);
  };

  const loadData = async (resetPage) => {
    setIsLoading(true);

    let filters = {
      limit: PAGE_SIZE,
      offset: resetPage ? 0 : paginationModel.page * PAGE_SIZE,
      sku: filter.sku,
      orderBy: getOrderBy()
    };
    if (filter.date) {
      filters.dateFrom = moment(filter.date).startOf('day').format();
      filters.dateTo = moment(filter.date).endOf('day').format();
    }
    const params = new URLSearchParams(filters);

    try {
      const response = await fetchWithInterceptor(
        `/api/sync/continue-selling/list?${params}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": authContext.getToken(),
          },
        }
      );
      const data = await response.json();
      setRows(data.items);
      setRowCount(data.total);
      if (resetPage) {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
      }
    } catch (error) {
      console.error("Failed to load data", error);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadData(false);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page]);

  useEffect(() => {
    loadData(true);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, sortModel]);

  const columns = [
    {
      field: "createdAt",
      headerName: "Updated at",
      flex: 1,
      sortable: true,
      valueGetter: (value) => value
          ? format(new Date(value), "yyyy-MM-dd hh:mm a")
          : ""
    },
    {
      field: "sku",
      headerName: "SKU",
      flex: 1,
      sortable: true,
    },
    {
      field: "status",
      headerName: "Continue Selling",
      flex: 1,
      sortable: true,
      renderCell: (params) => {
        return (
          <Chip
            label={params.value === "continue" ? "ALLOW" : "DENY"}
            size="small"
            variant="filled"
            color={params.value === "continue" ? "success" : "warning"} 
          />
        );
      }
    },
    {
      field: "cin7",
      headerName: "Cin7",
      flex: 1,
      sortable: true,
      valueGetter: (value) => !!value ? "Modified" : "Not modified"
    },
    {
      field: "shopifyB2c",
      headerName: "Shopify B2C",
      flex: 1,
      sortable: true,
      valueGetter: (value) => !!value ? "Modified" : "Not modified"
    },
    {
      field: "shopifyB2b",
      headerName: "Shopify B2B",
      flex: 1,
      sortable: true,
      valueGetter: (value) => !!value ? "Modified" : "Not modified"
    },
  ];

  return (
    <>
      <Typography variant="body1" sx={{ ml: 3, mt: 3, mr: 5, mb: 1, color: "text.secondary" }}>
        History of changes made automatically by the ZYN web application based on the stock status of items in AGLC.
      </Typography>
      <Container component="main" maxWidth="100%" sx={{ pb: 3 }}>
        <Paper sx={{ width: "100%", bgcolor: "background.paper", mt: 2, mb: 2 }}>
          <div style={{ padding: 16 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <DatePicker
                  label="Updated at"
                  value={filter.date}
                  onChange={(date) => setFilter({ ...filter, date })}
                  format="yyyy-MM-dd"
                  slotProps={{ textField: { variant: "outlined", fullWidth: true } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  label="SKU"
                  placeholder="Eg: 99001"
                  variant="outlined"
                  value={filter.sku}
                  onChange={(e) =>
                    setFilter({ ...filter, sku: e.target.value })
                  }
                  color="primary"
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: "auto" }} sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button onClick={handleReset}>
                  RESET FILTERS
                </Button>
              </Grid>
            </Grid>
          </div>
          <Divider />

          <CustomDataGrid
            rows={rows}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            rowCount={rowCount}
            loading={isLoading}
            onSortModelChange={(model) => setSortModel(model)}
            sortingMode="server"
          />
        </Paper>
      </Container>
    </>

  );
}

export default ContinueSellingChangeLog